import React, { createContext, useContext, useState, useEffect } from 'react';
import { QuickReply, ResponsePattern, WelcomeMessage } from '../types';
import { getSupabaseClient } from '../lib/supabase';

interface AdminContextType {
  isAuthenticated: boolean;
  login: (password: string) => boolean;
  logout: () => void;
  quickReplies: QuickReply[];
  responsePatterns: ResponsePattern[];
  welcomeMessages: WelcomeMessage[];
  updateQuickReplies: (replies: QuickReply[]) => Promise<void>;
  updateResponsePatterns: (patterns: ResponsePattern[]) => Promise<void>;
  updateWelcomeMessages: (messages: WelcomeMessage[]) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

let ADMIN_PASSWORD = '611655';

// Default data with proper UUID format (only hexadecimal characters)
const defaultQuickReplies: QuickReply[] = [
  { id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890', text: 'Şikayet başvurusu nasıl yapılır?', color: 'blue', category: 'general', response: 'Şikayet başvurusu nasıl yapılır?', variant: 'primary' },
  { id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012', text: 'Park aktiviteleri nelerdir?', color: 'purple', category: 'general', response: 'Park aktiviteleri nelerdir?', variant: 'primary' },
  { id: 'c3d4e5f6-a7b8-9012-cdef-345678901234', text: 'Başkan randevusu nasıl alınır?', color: 'red', category: 'general', response: 'Başkan randevusu nasıl alınır?', variant: 'primary' },
  { id: 'd4e5f6a7-b8c9-0123-def4-56789012345b', text: 'Belediye hizmetleri nelerdir?', color: 'green', category: 'general', response: 'Belediye hizmetleri nelerdir?', variant: 'primary' },
  { id: 'e5f6a7b8-c9d0-1234-ef56-789012345bcd', text: 'İletişim bilgileri nelerdir?', color: 'teal', category: 'general', response: 'İletişim bilgileri nelerdir?', variant: 'primary' },
  { id: 'f6a7b8c9-d0e1-2345-f678-90123456bcde', text: 'Çöp toplama saatleri nedir?', color: 'yellow', category: 'general', response: 'Çöp toplama saatleri nedir?', variant: 'primary' },
  { id: 'a7b8c9d0-e1f2-3456-789a-bcdef0123456', text: 'Su kesintisi var mı?', color: 'pink', category: 'general', response: 'Su kesintisi var mı?', variant: 'primary' },
  { id: 'b8c9d0e1-f2a3-4567-89ab-cdef01234567', text: 'Vergi ödemeleri nasıl yapılır?', color: 'indigo', category: 'general', response: 'Vergi ödemeleri nasıl yapılır?', variant: 'primary' },
];

const defaultResponsePatterns: ResponsePattern[] = [
  {
    id: 'd4e5f6a7-b8c9-0123-def4-56789012345a',
    trigger: 'şikayet, başvuru, problem',
    response: 'Şikayetinizi 0462 123 45 67 numaralı telefona iletebilir veya belediye binasına gelerek dilekçe verebilirsiniz.'
  },
  {
    id: 'e5f6a7b8-c9d0-1234-ef56-789012345abc',
    trigger: 'park, aktivite, etkinlik',
    response: 'Park aktiviteleri hakkında bilgi almak için 0462 123 45 67 numaralı telefonu arayabilirsiniz.'
  },
  {
    id: 'f6a7b8c9-d0e1-2345-f678-90123456abcd',
    trigger: 'başkan, randevu, görüşme',
    response: 'Başkan randevusu için 0462 123 45 67 numaralı telefonu arayarak sekreterlik ile iletişime geçebilirsiniz.'
  },
];

const defaultWelcomeMessages: WelcomeMessage[] = [
  { id: '01a2b3c4-d5e6-7890-abcd-ef1234567890', message: 'Gürsu AI Asistan size nasıl yardımcı olabilir? Hoş geldiniz!', is_active: true },
  { id: '02b3c4d5-e6f7-8901-bcde-f23456789012', message: 'Merhaba! Gürsu Belediyesi AI asistanıyım. Size nasıl yardımcı olabilirim?', is_active: false },
  { id: '03c4d5e6-f7a8-9012-cdef-345678901234', message: 'Hoş geldiniz! Belediye hizmetleri hakkında sorularınızı yanıtlamaya hazırım.', is_active: false },
  { id: '04d5e6f7-a8b9-0123-def4-56789012345b', message: 'Selam! Gürsu AI Asistan olarak size yardımcı olmak için buradayım.', is_active: false },
  { id: '05e6f7a8-b9c0-1234-ef56-789012345bcd', message: 'Merhaba dostum! Belediye işleriniz için ne yapabilirim?', is_active: false },
];

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>(defaultQuickReplies);
  const [responsePatterns, setResponsePatterns] = useState<ResponsePattern[]>(defaultResponsePatterns);
  const [welcomeMessages, setWelcomeMessages] = useState<WelcomeMessage[]>(defaultWelcomeMessages);

  useEffect(() => {
    loadAdminPin();
    fetchData();

    // Real-time subscriptions for instant updates
    const supabase = getSupabaseClient();
    if (supabase) {
      const quickRepliesChannel = supabase
        .channel('quick_replies_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'quick_replies' },
          (payload) => {
            console.log('Quick replies changed:', payload);
            fetchData();
          }
        )
        .subscribe();

      const responsePatternsChannel = supabase
        .channel('response_patterns_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'response_patterns' },
          (payload) => {
            console.log('Response patterns changed:', payload);
            fetchData();
          }
        )
        .subscribe();

      const welcomeMessagesChannel = supabase
        .channel('welcome_messages_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'welcome_messages' },
          (payload) => {
            console.log('Welcome messages changed:', payload);
            fetchData();
          }
        )
        .subscribe();

      // Cleanup subscriptions on unmount
      return () => {
        supabase.removeChannel(quickRepliesChannel);
        supabase.removeChannel(responsePatternsChannel);
        supabase.removeChannel(welcomeMessagesChannel);
      };
    }
  }, []);

  const loadAdminPin = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return;
    }

    try {
      const { data } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'admin_pin')
        .maybeSingle();

      if (data?.setting_value?.pin) {
        ADMIN_PASSWORD = data.setting_value.pin;
      }
    } catch (error) {
      console.error('Error loading admin PIN:', error);
    }
  };

  const fetchData = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      console.log('Using default data - Supabase not configured');
      return;
    }

    try {
      const [repliesResponse, patternsResponse, welcomeResponse] = await Promise.all([
        supabase.from('quick_replies').select('*'),
        supabase.from('response_patterns').select('*'),
        supabase.from('welcome_messages').select('*')
      ]);

      if (repliesResponse.data && repliesResponse.data.length > 0) {
        setQuickReplies(repliesResponse.data);
      }
      if (patternsResponse.data && patternsResponse.data.length > 0) {
        setResponsePatterns(patternsResponse.data);
      }
      if (welcomeResponse.data && welcomeResponse.data.length > 0) {
        setWelcomeMessages(welcomeResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const login = (password: string): boolean => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
  };

  const updateQuickReplies = async (replies: QuickReply[]) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setQuickReplies(replies);
      return;
    }

    try {
      const { data: existingReplies } = await supabase.from('quick_replies').select('id');
      const existingIds = existingReplies?.map(r => r.id) || [];
      const newIds = replies.map(r => r.id);

      const toDelete = existingIds.filter(id => !newIds.includes(id));
      if (toDelete.length > 0) {
        await supabase.from('quick_replies').delete().in('id', toDelete);
      }

      for (const reply of replies) {
        const replyData = {
          id: reply.id,
          text: reply.text,
          color: reply.color,
          category: reply.category || 'general',
          response: reply.response || reply.text,
          variant: reply.variant || 'primary',
          created_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('quick_replies')
          .upsert(replyData, { onConflict: 'id' });

        if (error) {
          console.error('Error upserting reply:', error);
          throw error;
        }
      }

      setQuickReplies(replies);
    } catch (error) {
      console.error('Error updating quick replies:', error);
      throw error;
    }
  };

  const updateResponsePatterns = async (patterns: ResponsePattern[]) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setResponsePatterns(patterns);
      return;
    }

    try {
      const { data: existingPatterns } = await supabase.from('response_patterns').select('id');
      const existingIds = existingPatterns?.map(p => p.id) || [];
      const newIds = patterns.map(p => p.id);

      const toDelete = existingIds.filter(id => !newIds.includes(id));
      if (toDelete.length > 0) {
        await supabase.from('response_patterns').delete().in('id', toDelete);
      }

      for (const pattern of patterns) {
        const patternData = {
          id: pattern.id,
          trigger: pattern.trigger,
          response: pattern.response,
          created_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('response_patterns')
          .upsert(patternData, { onConflict: 'id' });

        if (error) {
          console.error('Error upserting pattern:', error);
          throw error;
        }
      }

      setResponsePatterns(patterns);
    } catch (error) {
      console.error('Error updating response patterns:', error);
      throw error;
    }
  };

  const updateWelcomeMessages = async (messages: WelcomeMessage[]) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setWelcomeMessages(messages);
      return;
    }

    try {
      console.log('Updating welcome messages:', messages);

      const { data: existingMessages } = await supabase.from('welcome_messages').select('id');
      const existingIds = existingMessages?.map(m => m.id) || [];
      const newIds = messages.map(m => m.id);

      const toDelete = existingIds.filter(id => !newIds.includes(id));
      if (toDelete.length > 0) {
        await supabase.from('welcome_messages').delete().in('id', toDelete);
      }

      for (const message of messages) {
        const messageData = {
          id: message.id,
          message: message.message,
          is_active: message.is_active,
          created_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('welcome_messages')
          .upsert(messageData, { onConflict: 'id' });

        if (error) {
          console.error('Error upserting welcome message:', error);
          throw error;
        }
      }

      setWelcomeMessages(messages);
      console.log('Welcome messages updated successfully');
    } catch (error) {
      console.error('Error updating welcome messages:', error);
      throw error;
    }
  };

  return (
    <AdminContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        quickReplies,
        responsePatterns,
        welcomeMessages,
        updateQuickReplies,
        updateResponsePatterns,
        updateWelcomeMessages,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};